import * as React from 'react'
import InputAdornment from '@mui/material/InputAdornment'
import { parse } from 'date-fns'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import { string, object, date, number, type InferType, array } from 'yup'
import AwesomeDebouncePromise from 'awesome-debounce-promise'
import { ControlProps, MenuProps } from 'react-select'

import {
  CurrencyField,
  DatePicker,
  GroupBase,
  AsyncCreatableSelect,
  Select,
  LocaleProvider,
  components,
} from '@clara/ui'
import { useLocaleContext, useT } from '@clara/locale'
import { Dict, Omit, isEmpty, isString, runIfFn } from '@clara/utils'
import { useAsyncFn } from '@clara/hooks'
import { settings } from '@/config'

import { DropdownOption, Merchant } from '@/types/api'
import { yupResolver } from '@hookform/resolvers/yup'
import { getErrorHelperText } from '@/utils/form'
import { Box, Typography } from '@mui/material'
import { createMerchant, getMerchants } from '../api/metadata'
import { MetadataOptions } from '../hooks/use-metadata'
import { map } from '../utils/map'
import { LabelsInput } from '@/components/labels-input'

const MINIMUM_MERCHANT_CHARACTERS = 2

type MerchantOption = Readonly<{
  label: string
  value: string
  __entity__: Merchant
}>

type MerchantCategoryOption = Omit<MerchantOption, '__entity__'>

export interface ManageReimbursementFormProps {
  /**
   * The children nodes to render inside the form.
   * This could include form fields, buttons, or any other React nodes.
   */
  children?: React.ReactNode

  /**
   * Optional unique identifier for the form.
   * Can be used for accessibility or form tracking purposes.
   */
  id?: string

  /**
   * Default values for the form fields.
   * Allows the form to be initialized with pre-populated data.
   */
  defaultValues?: Partial<ManageFormValues>

  /**
   * Callback function that is invoked when the form is submitted.
   * Receives the form values as a parameter.
   *
   * @param values - The current form values at the time of submission.
   */
  onSubmit?: (values: ManageFormValues) => void

  /**
   * Callback function that is triggered when the amount field changes.
   * Receives the new amount value as a parameter.
   *
   * @param value - The new amount entered in the form.
   */
  onAmountChange?: (value: number) => void

  /**
   * Metadata containing merchants and categories to be used within the form.
   * Provides necessary options for merchant and category selection fields.
   */
  metadata: MetadataOptions & {
    merchants: MerchantOption[]
    categories: MerchantCategoryOption[]
    labels: DropdownOption[]
    labelsDict: Dict<DropdownOption>
    labelsAreLoading: boolean
    reimbursementsLabelsIsEnabled?: boolean
    /**
     * Maximum allowed length for the description field.
     * Limits the number of characters that can be entered in the description.
     * @defaults to 200
     */
    max_transfer_amount: number
    /**
     * Maximum allowed amount for the form.
     * This is used to restrict the amount field input to a certain upper limit.
     */
    max_description_length: number
    /**
     * Currency code for the country.
     * Defines the official currency code (ISO 4217) used in the country.
     * Example: 'MXN' for Mexico, 'BRL' for Brazil.
     */
    currency_code: string
  }
}

type ManageMetadata = ManageReimbursementFormProps['metadata']

type ManageSchemaProps = Pick<
  ManageMetadata,
  'max_description_length' | 'max_transfer_amount'
>
type ManageSchema = ReturnType<typeof getManageSchema>

export type ManageFormValues = InferType<ManageSchema>

const getManageSchema = ({
  max_description_length,
  max_transfer_amount,
}: ManageSchemaProps) => {
  const schema = object().shape({
    amount: number()
      .transform((value) => (Number.isNaN(value) ? null : value))
      .positive()
      .min(0.1)
      .max(max_transfer_amount)
      .required(),
    category: string().required(),
    expenseDate: date().required(),
    description: string().min(0).max(max_description_length),
    merchant: string().required(),
    labels: array().of(string().required()).optional(),
    // This is only utilized for merchant lookup in update scenarios, not for creation.
    __merchant_name__: string().optional(),
  })

  return schema
}

const strip = (x: string) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

const regex = (p: string) => new RegExp(p, 'i')

const apply = (fn: (x: string) => string) => (y: string) => fn(y)

const normalize = apply(strip)

const match = (a: string) => {
  const r = regex(normalize(a))
  return (b: string) => r.test(normalize(b))
}

const CustomMenu = (props: MenuProps<any, false, any>) => {
  const { inputValue } = props.selectProps
  if (inputValue.trim().length <= MINIMUM_MERCHANT_CHARACTERS) {
    return null
  }

  return <components.Menu {...props} />
}

const CustomControl = ({ children, ...props }: ControlProps<any, false>) => (
  <Box
    sx={{
      'input.async-creatable-merchant__input': { opacity: '1 !important' },
    }}
  >
    <components.Control {...props}>{children}</components.Control>
  </Box>
)

export const ManageReimbursementForm = React.forwardRef(function Form(
  props: React.PropsWithChildren<ManageReimbursementFormProps>,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  const {
    onAmountChange,
    onSubmit: onSubmitProp,
    defaultValues = {},
    metadata,
    ...rest
  } = props
  const { t } = useT(['reimbursements', 'approvals-flow'])
  const { i18nLocale } = useLocaleContext()
  const { control, handleSubmit, watch, setValue } = useForm<ManageFormValues>({
    defaultValues: {
      ...defaultValues,
      expenseDate: isString(defaultValues?.expenseDate)
        ? parse(defaultValues.expenseDate, 'yyyy-MM-dd', new Date())
        : defaultValues.expenseDate,
    },
    resolver: yupResolver<ManageFormValues>(getManageSchema(metadata)),
  })

  const amount = watch('amount')
  const { merchants: defaultOptions = [] } = metadata
  const [option, setOption] = React.useState<MerchantOption | null>(null)
  const [inputValue, setInputValue] = React.useState('')
  React.useEffect(() => {
    runIfFn(onAmountChange, amount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount])

  const [_, searchMerchant] = useAsyncFn(async (merchantName: string) => {
    const results = await getMerchants({
      countryCode: metadata.country_code,
      merchantLang: metadata.lang,
      name: merchantName,
    }).then((merchants = []) =>
      map(merchants, (merchant) => ({
        value: merchant.code,
        label: merchant.name,
        __entity__: merchant,
      })),
    )

    return results
  }, [])

  React.useEffect(() => {
    async function initSearch() {
      const { __merchant_name__: merchant } = defaultValues
      if (!merchant) return

      const matchWith = match(merchant.toLowerCase())
      const found = defaultOptions.find((m) => matchWith(m.label))
      if (found) return setOption(found)

      const matches =
        (await searchMerchant(merchant).then((merchants) =>
          merchants.find((m) => matchWith(m.label)),
        )) ?? null

      setOption(matches)
    }

    initSearch()
  }, [defaultValues, defaultOptions, searchMerchant])

  React.useEffect(() => {
    setInputValue(option?.label ?? '')
  }, [option])

  const onSubmit: SubmitHandler<ManageFormValues> = React.useCallback(
    (values) => {
      runIfFn(onSubmitProp, values)
    },
    [onSubmitProp],
  )

  const [{ loading: isLoading }, onCreateOption] = useAsyncFn(
    async (merchantName: string) => {
      const merchant = await createMerchant({
        name: merchantName,
        countryCode: metadata.country_code,
        categoryKey: null,
        description: '',
      })

      const option = {
        label: merchant.name,
        value: merchant.code,
        __entity__: merchant,
      }

      return option
    },
    [metadata.country_code],
  )

  const loadOptions = React.useMemo(() => {
    return AwesomeDebouncePromise(async (value: string) => {
      try {
        const matchWith = match(value.toLowerCase())

        //  check if it is in the 'defaultOptions'
        const found = defaultOptions.filter((merchant) =>
          matchWith(merchant.label),
        )
        if (found.length) return found

        const results = await searchMerchant(value)

        const matches = results.filter((merchant) =>
          matchWith(merchant.label.toLowerCase()),
        )
        return matches
      } catch (err) {
        return []
      }
    }, 500)
  }, [defaultOptions, searchMerchant])

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      spacing={24 / 8}
      {...rest}
    >
      {props.children}
      <Controller
        name="amount"
        control={control}
        render={({ field, fieldState }) => (
          <CurrencyField
            {...field}
            inputRef={ref}
            label={t('create.request.form.amount')}
            error={Boolean(fieldState.error)}
            helperText={getErrorHelperText(t, fieldState.error)}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {settings.currency_code}
                </InputAdornment>
              ),
              startAdornment: (
                <InputAdornment position="end">
                  {settings.currency_symbol}
                </InputAdornment>
              ),
            }}
            inputProps={{
              allowNegative: false,
              fixedDecimalScale: true,
              decimalScale: 2,
              decimalSeparator: settings.decimal_separator,
              thousandSeparator: settings.thousand_separator,
              isAllowed: (values) => {
                if (!values.value) return true
                return values?.floatValue! <= metadata.max_transfer_amount
              },
            }}
          />
        )}
      />
      <Controller
        name="expenseDate"
        control={control}
        render={({ field, fieldState }) => (
          <DatePicker
            locale={i18nLocale}
            label={t('create.request.form.date')}
            value={field.value}
            onChange={(nextDate) => {
              field.onChange(nextDate ?? null)
            }}
            error={Boolean(fieldState.error)}
            helperText={getErrorHelperText(t, fieldState.error)}
          />
        )}
      />
      {/* TODO: refactor this to another component */}
      <Controller
        name="merchant"
        control={control}
        render={({ field, fieldState }) => (
          <LocaleProvider
            locale={{
              select: {
                noResultsMsg: t('components.multi-select.noResultsMsg', {
                  ns: 'approvals-flow',
                }),
                placeholder: t('create.request.form.category'),
                create: t('components.multi-select.create', {
                  ns: 'approvals-flow',
                }),
                allOption: {
                  item: '',
                  item_other: '',
                  counter: '',
                  zero: '',
                  selected: '',
                  indeterminate: '',
                  valueSelected: '',
                },
              },
            }}
          >
            <AsyncCreatableSelect<
              MerchantOption,
              false,
              GroupBase<MerchantOption>
            >
              isLoading={isLoading}
              label={t('create.request.form.merchant')}
              placeholder={t('create.request.form.merchant')}
              defaultOptions={defaultOptions}
              value={option}
              onChange={(option) => {
                setOption(option)
                setInputValue(option?.label ?? '')

                // update the form values
                field.onChange(option?.value)
                setValue('category', option?.__entity__.categoryKey ?? '')
              }}
              loadOptions={(value: string) => {
                return value.length > MINIMUM_MERCHANT_CHARACTERS
                  ? loadOptions(value)
                  : Promise.resolve([])
              }}
              error={!!fieldState.error}
              helperText={getErrorHelperText(t, fieldState.error)}
              onCreateOption={async (value) => {
                onCreateOption(value).then((option) => {
                  if (!option) return
                  setOption(option)

                  // update the form values
                  field.onChange(option.value)
                  setValue('category', option.__entity__.categoryKey ?? '')

                  return option
                })
              }}
              isClearable
              styles={{
                dropdownIndicator: (base) => ({
                  ...base,
                  display: 'none',
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  paddingRight: '0.75rem',
                  '.MuiDivider-root': {
                    display: 'none',
                  },
                  indicatorSeparator: {
                    display: 'none',
                  },
                }),
              }}
              isValidNewOption={(inputValue) =>
                inputValue.trim().length > MINIMUM_MERCHANT_CHARACTERS
              }
              components={{
                Menu: CustomMenu,
                Control: CustomControl,
              }}
              backspaceRemovesValue={false}
              controlShouldRenderValue={true}
              classNamePrefix="async-creatable-merchant"
              inputValue={inputValue}
              onInputChange={(value, { action }) => {
                if (action === 'input-change') {
                  setInputValue(value)
                  if (isEmpty(value)) {
                    setOption(null)
                    field.onChange(undefined)
                  }
                }
              }}
              onBlur={() => {
                if (option?.value) {
                  setInputValue(option.label)
                } else {
                  setOption(null)
                  setInputValue('')
                }
              }}
            />
          </LocaleProvider>
        )}
      />
      <Controller
        name="category"
        control={control}
        render={({ field, fieldState }) => (
          <Select<
            MerchantCategoryOption,
            false,
            GroupBase<MerchantCategoryOption>
          >
            label={t('create.request.form.category')}
            options={metadata.categories}
            value={
              metadata.categories.find(
                (merchant) => merchant.value === field.value,
              ) || null
            }
            onChange={(newValue) => {
              field.onChange(newValue?.value)
            }}
            error={!!fieldState.error}
            helperText={getErrorHelperText(t, fieldState.error)}
          />
        )}
      />
      {metadata.reimbursementsLabelsIsEnabled && (
        <Controller
          name="labels"
          control={control}
          render={({ field, fieldState }) => (
            <LabelsInput
              field={field}
              fieldState={fieldState}
              labels={metadata.labels}
              labelsDict={metadata.labelsDict}
              isLoading={metadata.labelsAreLoading}
              isClearable={true}
            />
          )}
        />
      )}
      <Stack>
        <Controller
          name="description"
          control={control}
          render={({ field, fieldState }) => (
            <Stack spacing={0.5}>
              <TextField
                {...field}
                error={!!fieldState.error}
                label={t('create.request.form.description')}
                placeholder={t('create.request.form.description')}
                aria-label={t('create.request.form.description')}
                helperText={getErrorHelperText(t, fieldState.error)}
                minRows={4}
                multiline
                sx={{
                  '.MuiInputBase-input': {
                    padding: '0',
                  },
                }}
                inputProps={{ maxLength: metadata.max_description_length }}
              />
              {Boolean(fieldState.error) === false && (
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    color: theme.palette.v3.content.low_emphasis,
                  })}
                >
                  {t('create.request.form.description.helper', {
                    count: field.value?.length ?? 0,
                    max: metadata.max_description_length,
                  })}
                </Typography>
              )}
            </Stack>
          )}
        />
      </Stack>
    </Stack>
  )
})
